// /app/frontend/src/components/security/MFASetup.jsx
// Setup-only MFA component — no self-service enable/disable
// Only rendered when admin has required MFA and user hasn't set it up
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Shield, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export default function MFASetup() {
  const { refreshMfaEnforcement } = useAuth();

  const [step, setStep] = useState('start'); // start | scan | backup
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await api.post('/mfa/setup');
      setSetupData(res.data);
      setStep('scan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initiate MFA setup');
    } finally { setLoading(false); }
  };

  const confirmSetup = async () => {
    if (verifyCode.length !== 6) return;
    setLoading(true);
    try {
      const res = await api.post('/mfa/verify-setup', { code: verifyCode });
      setBackupCodes(res.data.backup_codes);
      setStep('backup');
      await refreshMfaEnforcement();
      toast.success('MFA enabled successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code. Please try again.');
      setVerifyCode('');
    } finally { setLoading(false); }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setupData?.secret || '');
    toast.success('Secret key copied');
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied');
  };

  if (step === 'start') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-400">
          Set up two-factor authentication using an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, Authy, etc.)
        </p>
        <Button className="bg-violet-600 hover:bg-violet-700" size="sm" onClick={startSetup} disabled={loading} data-testid="start-mfa-setup-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
          Set Up MFA
        </Button>
      </div>
    );
  }

  if (step === 'scan') {
    return (
      <div className="space-y-5">
        <p className="text-sm text-slate-300 font-medium">Scan this QR code with your authenticator app:</p>
        {setupData?.qr_url && (
          <div className="flex flex-col items-center gap-3">
            <div className="p-3 bg-white rounded-xl">
              <img src={setupData.qr_url} alt="QR Code" className="w-48 h-48" data-testid="mfa-qr-code" />
            </div>
            <div className="w-full">
              <Label className="text-xs text-slate-400">Can't scan? Enter this key manually:</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <code className="flex-1 px-3 py-2 bg-slate-900 rounded-lg text-xs font-mono text-slate-300 border border-slate-700 break-all">
                  {setupData.secret}
                </code>
                <Button variant="ghost" size="icon" className="shrink-0 h-9 w-9" onClick={copySecret}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-slate-200">Enter the 6-digit code from your app</Label>
          <Input
            value={verifyCode}
            onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="bg-slate-900 border-slate-700 text-center text-2xl tracking-[0.4em] h-12"
            inputMode="numeric"
            autoComplete="one-time-code"
            data-testid="mfa-verify-code-input"
          />
        </div>
        <Button
          className="w-full bg-violet-600 hover:bg-violet-700"
          onClick={confirmSetup}
          disabled={verifyCode.length !== 6 || loading}
          data-testid="mfa-verify-confirm-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Verify & Enable MFA
        </Button>
      </div>
    );
  }

  // step === 'backup'
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-300 font-medium">Save these backup codes securely. Each can only be used once.</p>
      <div className="grid grid-cols-2 gap-2">
        {backupCodes.map((c, i) => (
          <code key={i} className="px-3 py-2 bg-slate-900 rounded-lg text-sm font-mono text-center text-slate-200 border border-slate-700">{c}</code>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={copyBackupCodes} className="w-full" data-testid="copy-backup-codes-btn">
        <Copy className="h-3.5 w-3.5 mr-2" /> Copy All Backup Codes
      </Button>
      <p className="text-xs text-emerald-400 text-center">MFA is now active on your account.</p>
    </div>
  );
}
